import { useState, type ReactNode } from "react";
import type { Box, DemoPhoto } from "./images";
import { EYEBROW, FOCUS, H2, SECTION_Y, WRAP } from "./ui";

/** A small viewfinder-bracket mark (decorative). */
export const BracketMark = ({ size = 18, color = "#EA8A23", thickness = 2 }: { size?: number; color?: string; thickness?: number }) => (
  <span
    aria-hidden="true"
    className="ph-br ph-br-mark"
    style={{ width: size, height: size, ["--c" as string]: color, ["--t" as string]: `${thickness}px` }}
  />
);

/** Brackets framing a box on a photo, positioned in % so they track the photo at any size. */
export const BoxBrackets = ({ box, color = "#EA8A23", arm = "30%", delay, pulse = true }: { box: Box; color?: string; arm?: string; delay?: string; pulse?: boolean }) => (
  <span
    aria-hidden="true"
    data-testid="demo-box"
    className={`ph-br${pulse ? " ph-lock" : ""}`}
    style={{
      left: `${box[0]}%`,
      top: `${box[1]}%`,
      width: `${box[2]}%`,
      height: `${box[3]}%`,
      ["--c" as string]: color,
      ["--l" as string]: arm,
      animationDelay: delay,
    }}
  />
);

// The demo's colours (the dashboard's tokens, on the light ground): the amber eyebrow, ink chips when on, outlined
// (the dashboard's outline button) when off.
const SKIN = {
  eyebrow: "text-accent-foreground",
  intro: "text-muted-foreground",
  chipOn: "border-foreground bg-foreground text-background",
  chipOff: "border-input bg-background text-foreground hover:border-foreground/40 hover:bg-accent",
} as const;

export interface DemoChip {
  id: string;
  label: string;
}

/**
 * The interactive demo: pick a chip and only the photos with that thing light up, framed by brackets; the rest
 * fade. Chips are real toggle buttons (aria-pressed) and the result line is a polite live region.
 */
const PhotoDemo = ({
  idPrefix,
  eyebrow,
  heading,
  intro,
  chips,
  chipsLabel,
  initial,
  photos,
  altOf,
  result,
  chipShape,
  chipClassName = "",
}: {
  idPrefix: string;
  eyebrow: string;
  heading: string;
  intro?: ReactNode;
  chips: DemoChip[];
  chipsLabel: string;
  initial: string;
  photos: DemoPhoto[];
  altOf: (photo: DemoPhoto) => string;
  result: (chip: DemoChip, count: number, total: number) => string;
  chipShape: "pill" | "key";
  chipClassName?: string;
}) => {
  const skin = SKIN;
  const [active, setActive] = useState(initial);
  const chip = chips.find((c) => c.id === active) ?? chips[0];
  const hits = photos.filter((p) => p.boxes[chip.id]).length;
  const headingId = `${idPrefix}-heading`;

  return (
    <section
      aria-labelledby={headingId}
      className={`${WRAP} ${SECTION_Y} flex flex-wrap items-start gap-12`}
    >
      <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-5">
        <span className={`${EYEBROW} ${skin.eyebrow}`}>{eyebrow}</span>
        <h2 id={headingId} className={H2}>
          {heading}
        </h2>
        {intro && <p className={`m-0 text-[17px] leading-relaxed ${skin.intro}`}>{intro}</p>}
        <div className="flex flex-wrap gap-2" role="group" aria-label={chipsLabel}>
          {chips.map((c) => {
            const on = c.id === chip.id;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                onClick={() => setActive(c.id)}
                className={`${chipShape === "pill" ? "h-[46px] rounded-full px-[18px] text-[15px] font-semibold" : "h-12 min-w-[76px] rounded-xl px-4 font-display text-lg font-bold tabular-nums"} border-2 transition-colors ${FOCUS} ${
                  on ? skin.chipOn : skin.chipOff
                } ${chipClassName}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <p aria-live="polite" data-testid={`${idPrefix}-result`} className="m-0 flex items-center gap-2.5 text-base font-semibold">
          <BracketMark size={20} thickness={2.5} />
          {result(chip, hits, photos.length)}
        </p>
      </div>
      <div className="min-w-0 flex-[999_1_560px] columns-2 gap-3 sm:columns-3">
        {photos.map((p) => {
          const box = p.boxes[chip.id];
          return (
            <div
              key={p.id}
              data-hit={box ? "true" : "false"}
              className={`ph-fade relative mb-3 overflow-hidden rounded-lg transition-opacity duration-300 [break-inside:avoid] ${box ? "opacity-100" : "opacity-[0.28]"}`}
            >
              <img
                src={p.src}
                srcSet={p.srcSet}
                sizes="(min-width: 1024px) 260px, 50vw"
                width={p.width}
                height={p.height}
                alt={altOf(p)}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full"
              />
              {box && <BoxBrackets box={box} arm={chipShape === "key" ? "34%" : "30%"} />}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PhotoDemo;
